import { colorEngine } from './colorEngine';

export interface ImportResult {
  colors: string[];
  name?: string;
  error?: string;
}

export type ImportFormat = 'ase' | 'aco' | 'gpl' | 'css' | 'json' | 'tailwind' | 'auto';

export class ImportFormats {
  /**
   * Auto-detect format from file extension or content
   */
  detectFormat(filename: string, content: string | ArrayBuffer): ImportFormat {
    const ext = filename.toLowerCase().split('.').pop() || '';
    
    // Extension-based detection
    if (ext === 'ase') return 'ase';
    if (ext === 'aco') return 'aco';
    if (ext === 'gpl') return 'gpl';
    if (ext === 'css' || ext === 'scss') return 'css';
    if (ext === 'json') return 'json';
    if (ext === 'js' || ext === 'ts') return 'tailwind';
    
    // Content-based detection for text files
    if (typeof content === 'string') {
      if (content.trim().startsWith('GIMP Palette')) return 'gpl';
      if (content.includes('module.exports') || content.includes('export default')) return 'tailwind';
      if (content.trim().startsWith('{') || content.trim().startsWith('[')) return 'json';
      if (content.includes(':root') || content.includes('--color') || content.includes('$color')) return 'css';
    }
    
    return 'auto';
  }

  /**
   * Import palette from file
   */
  async importFromFile(file: File): Promise<ImportResult> {
    const filename = file.name;
    const isBinary = filename.toLowerCase().endsWith('.ase') || filename.toLowerCase().endsWith('.aco');
    
    try {
      if (isBinary) {
        const arrayBuffer = await file.arrayBuffer();
        const format = this.detectFormat(filename, arrayBuffer);
        
        if (format === 'ase') {
          return this.importASE(arrayBuffer, filename);
        } else if (format === 'aco') {
          return this.importACO(arrayBuffer, filename);
        }
        
        return { colors: [], error: 'Unsupported binary format' };
      } else {
        const text = await file.text();
        const format = this.detectFormat(filename, text);
        
        switch (format) {
          case 'gpl':
            return this.importGPL(text, filename);
          case 'css':
            return this.importCSS(text, filename);
          case 'json':
            return this.importJSON(text, filename);
          case 'tailwind':
            return this.importTailwind(text, filename);
          default:
            // Try all text formats
            return this.importAuto(text, filename);
        }
      }
    } catch (error) {
      return {
        colors: [],
        error: `Failed to import: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Import Adobe Swatch Exchange (.ase) format
   */
  importASE(buffer: ArrayBuffer, filename: string): ImportResult {
    try {
      const view = new DataView(buffer);
      let offset = 0;

      // Read signature (4 bytes: "ASEF")
      const signature = String.fromCharCode(
        view.getUint8(offset++),
        view.getUint8(offset++),
        view.getUint8(offset++),
        view.getUint8(offset++)
      );

      if (signature !== 'ASEF') {
        return { colors: [], error: 'Invalid ASE file signature' };
      }

      // Read version (2 bytes major, 2 bytes minor)
      const versionMajor = view.getUint16(offset);
      offset += 2;
      const versionMinor = view.getUint16(offset);
      offset += 2;

      // Validate version (currently unused but read for future compatibility)
      if (versionMajor > 1 || versionMinor > 0) {
        console.warn(`Unsupported ASE version ${versionMajor}.${versionMinor}`);
      }

      // Read number of blocks (4 bytes)
      const numBlocks = view.getUint32(offset);
      offset += 4;

      const colors: string[] = [];

      for (let i = 0; i < numBlocks; i++) {
        // Read block type (2 bytes)
        const blockType = view.getUint16(offset);
        offset += 2;

        // Read block length (4 bytes)
        const blockLength = view.getUint32(offset);
        offset += 4;

        const blockStart = offset;

        if (blockType === 0x0001) {
          // Color entry
          // Read name length (2 bytes)
          const nameLength = view.getUint16(offset);
          offset += 2;

          // Skip name (UTF-16, 2 bytes per char)
          offset += nameLength * 2;

          // Read color model (4 bytes ASCII)
          const colorModel = String.fromCharCode(
            view.getUint8(offset++),
            view.getUint8(offset++),
            view.getUint8(offset++),
            view.getUint8(offset++)
          );

          if (colorModel === 'RGB ') {
            // Read RGB values (3 x 4 bytes float32)
            const r = view.getFloat32(offset);
            offset += 4;
            const g = view.getFloat32(offset);
            offset += 4;
            const b = view.getFloat32(offset);
            offset += 4;

            // Convert to hex
            const hex = this.rgbToHex(
              Math.round(r * 255),
              Math.round(g * 255),
              Math.round(b * 255)
            );
            colors.push(hex);
          } else if (colorModel === 'CMYK') {
            // Read CMYK values (4 x 4 bytes float32)
            const c = view.getFloat32(offset);
            offset += 4;
            const m = view.getFloat32(offset);
            offset += 4;
            const y = view.getFloat32(offset);
            offset += 4;
            const k = view.getFloat32(offset);
            offset += 4;

            // Convert CMYK to RGB
            const hex = this.cmykToHex(c, m, y, k);
            colors.push(hex);
          } else if (colorModel === 'LAB ') {
            // Read LAB values (3 x 4 bytes float32)
            const l = view.getFloat32(offset);
            offset += 4;
            const a = view.getFloat32(offset);
            offset += 4;
            const b = view.getFloat32(offset);
            offset += 4;

            // Convert LAB to RGB (simplified conversion)
            const hex = this.labToHex(l * 100, a * 100, b * 100);
            colors.push(hex);
          }

          // Read color type (2 bytes) - skip
          offset += 2;
        }

        // Ensure we're at the end of the block
        offset = blockStart + blockLength;
      }

      return {
        colors,
        name: filename.replace(/\.ase$/i, ''),
      };
    } catch (error) {
      return {
        colors: [],
        error: `ASE parse error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Import Adobe Color (.aco) format
   */
  importACO(buffer: ArrayBuffer, filename: string): ImportResult {
    try {
      const view = new DataView(buffer);
      let offset = 0;

      // Read version (2 bytes)
      const version = view.getUint16(offset);
      offset += 2;

      // Validate version (currently unused but read for future compatibility)
      if (version > 1) {
        console.warn(`Unsupported SBP version ${version}`);
      }

      // Read number of colors (2 bytes)
      const numColors = view.getUint16(offset);
      offset += 2;

      const colors: string[] = [];

      for (let i = 0; i < numColors; i++) {
        // Read color space (2 bytes)
        const colorSpace = view.getUint16(offset);
        offset += 2;

        if (colorSpace === 0) {
          // RGB color space
          // Read RGB values (3 x 2 bytes, 0-65535 range)
          const r = view.getUint16(offset);
          offset += 2;
          const g = view.getUint16(offset);
          offset += 2;
          const b = view.getUint16(offset);
          offset += 2;

          // Skip padding (2 bytes)
          offset += 2;

          // Convert from 0-65535 to 0-255
          const hex = this.rgbToHex(
            Math.round((r / 65535) * 255),
            Math.round((g / 65535) * 255),
            Math.round((b / 65535) * 255)
          );
          colors.push(hex);
        } else if (colorSpace === 1) {
          // HSB color space
          const h = view.getUint16(offset);
          offset += 2;
          const s = view.getUint16(offset);
          offset += 2;
          const b = view.getUint16(offset);
          offset += 2;
          offset += 2; // padding

          // Convert HSB to RGB
          const hex = this.hsbToHex(h / 65535, s / 65535, b / 65535);
          colors.push(hex);
        } else if (colorSpace === 2) {
          // CMYK color space
          const c = view.getUint16(offset);
          offset += 2;
          const m = view.getUint16(offset);
          offset += 2;
          const y = view.getUint16(offset);
          offset += 2;
          const k = view.getUint16(offset);
          offset += 2;

          // Convert from 0-65535 to 0-1
          const hex = this.cmykToHex(
            c / 65535,
            m / 65535,
            y / 65535,
            k / 65535
          );
          colors.push(hex);
        } else {
          // Unknown color space, skip
          offset += 8;
        }
      }

      return {
        colors,
        name: filename.replace(/\.aco$/i, ''),
      };
    } catch (error) {
      return {
        colors: [],
        error: `ACO parse error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Import GIMP Palette (.gpl) format
   */
  importGPL(content: string, filename: string): ImportResult {
    try {
      const lines = content.split('\n');
      const colors: string[] = [];
      let name = filename.replace(/\.gpl$/i, '');

      for (const line of lines) {
        const trimmed = line.trim();

        // Skip comments and empty lines
        if (!trimmed || trimmed.startsWith('#')) continue;

        // Parse name
        if (trimmed.startsWith('Name:')) {
          name = trimmed.substring(5).trim();
          continue;
        }

        // Skip header lines
        if (trimmed.startsWith('GIMP Palette') || trimmed.startsWith('Columns:')) {
          continue;
        }

        // Parse color line: "R G B Name"
        const parts = trimmed.split(/\s+/);
        if (parts.length >= 3) {
          const r = parseInt(parts[0], 10);
          const g = parseInt(parts[1], 10);
          const b = parseInt(parts[2], 10);

          if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
            colors.push(this.rgbToHex(r, g, b));
          }
        }
      }

      if (colors.length === 0) {
        return { colors: [], error: 'No valid colors found in GPL file' };
      }

      return { colors, name };
    } catch (error) {
      return {
        colors: [],
        error: `GPL parse error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Import CSS/SCSS variables
   */
  importCSS(content: string, filename: string): ImportResult {
    try {
      const colors: string[] = [];
      const name = filename.replace(/\.(css|scss)$/i, '');

      // Match CSS custom properties: --name: value;
      const cssVarRegex = /--[\w-]+:\s*([^;]+);/g;
      let match;

      while ((match = cssVarRegex.exec(content)) !== null) {
        const value = match[1].trim();
        const parsed = colorEngine.parseColor(value);
        if (parsed) {
          colors.push(parsed.hex);
        }
      }

      // Match SCSS variables: $name: value;
      const scssVarRegex = /\$[\w-]+:\s*([^;]+);/g;
      while ((match = scssVarRegex.exec(content)) !== null) {
        const value = match[1].trim();
        const parsed = colorEngine.parseColor(value);
        if (parsed) {
          colors.push(parsed.hex);
        }
      }

      if (colors.length === 0) {
        return { colors: [], error: 'No valid color values found in CSS/SCSS file' };
      }

      return { colors, name };
    } catch (error) {
      return {
        colors: [],
        error: `CSS parse error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Import JSON palette
   */
  importJSON(content: string, filename: string): ImportResult {
    try {
      const data = JSON.parse(content);
      const colors: string[] = [];
      let name = filename.replace(/\.json$/i, '');

      // Try to extract name
      if (data.name && typeof data.name === 'string') {
        name = data.name;
      }

      // Try different JSON structures
      if (Array.isArray(data)) {
        // Simple array of colors
        for (const item of data) {
          if (typeof item === 'string') {
            const parsed = colorEngine.parseColor(item);
            if (parsed) colors.push(parsed.hex);
          } else if (item && typeof item === 'object' && 'color' in item) {
            const parsed = colorEngine.parseColor(item.color);
            if (parsed) colors.push(parsed.hex);
          }
        }
      } else if (data.colors && Array.isArray(data.colors)) {
        // Object with colors array (matches our PaletteRecord format)
        for (const color of data.colors) {
          const parsed = colorEngine.parseColor(color);
          if (parsed) colors.push(parsed.hex);
        }
      } else if (typeof data === 'object') {
        // Object with color properties
        for (const key in data) {
          if (typeof data[key] === 'string') {
            const parsed = colorEngine.parseColor(data[key]);
            if (parsed) colors.push(parsed.hex);
          }
        }
      }

      if (colors.length === 0) {
        return { colors: [], error: 'No valid colors found in JSON file' };
      }

      return { colors, name };
    } catch (error) {
      return {
        colors: [],
        error: `JSON parse error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Import Tailwind config
   */
  importTailwind(content: string, filename: string): ImportResult {
    try {
      const colors: string[] = [];
      const name = filename.replace(/\.(js|ts)$/i, '');

      // Extract the colors object from the config
      // This is a simplified parser - a full parser would need to evaluate JS
      
      // Match color definitions in various formats
      const colorPatterns = [
        // 'color-name': '#hex'
        /['"][\w-]+['"]\s*:\s*['"]([^'"]+)['"]/g,
        // color-name: '#hex'
        /([\w-]+)\s*:\s*['"]([^'"]+)['"]/g,
      ];

      for (const pattern of colorPatterns) {
        let match;
        while ((match = pattern.exec(content)) !== null) {
          const value = match[match.length - 1]; // Last capture group is the color value
          const parsed = colorEngine.parseColor(value);
          if (parsed) {
            colors.push(parsed.hex);
          }
        }
      }

      // Remove duplicates
      const uniqueColors = Array.from(new Set(colors));

      if (uniqueColors.length === 0) {
        return { colors: [], error: 'No valid colors found in Tailwind config' };
      }

      return { colors: uniqueColors, name };
    } catch (error) {
      return {
        colors: [],
        error: `Tailwind config parse error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Try all text-based formats automatically
   */
  private importAuto(content: string, filename: string): ImportResult {
    // Try formats in order of specificity
    const formats: Array<() => ImportResult> = [
      () => this.importGPL(content, filename),
      () => this.importJSON(content, filename),
      () => this.importTailwind(content, filename),
      () => this.importCSS(content, filename),
    ];

    for (const tryFormat of formats) {
      const result = tryFormat();
      if (result.colors.length > 0) {
        return result;
      }
    }

    return {
      colors: [],
      error: 'Could not detect file format. Supported formats: ASE, ACO, GPL, CSS, JSON, Tailwind config',
    };
  }

  // Helper conversion functions

  private rgbToHex(r: number, g: number, b: number): string {
    const toHex = (n: number) => {
      const hex = Math.max(0, Math.min(255, Math.round(n))).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private cmykToHex(c: number, m: number, y: number, k: number): string {
    // Convert CMYK (0-1) to RGB
    const r = 255 * (1 - c) * (1 - k);
    const g = 255 * (1 - m) * (1 - k);
    const b = 255 * (1 - y) * (1 - k);
    return this.rgbToHex(r, g, b);
  }

  private hsbToHex(h: number, s: number, b: number): string {
    // Convert HSB to RGB
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = b * (1 - s);
    const q = b * (1 - f * s);
    const t = b * (1 - (1 - f) * s);

    let r = 0, g = 0, bl = 0;
    switch (i % 6) {
      case 0: r = b; g = t; bl = p; break;
      case 1: r = q; g = b; bl = p; break;
      case 2: r = p; g = b; bl = t; break;
      case 3: r = p; g = q; bl = b; break;
      case 4: r = t; g = p; bl = b; break;
      case 5: r = b; g = p; bl = q; break;
    }

    return this.rgbToHex(r * 255, g * 255, bl * 255);
  }

  private labToHex(l: number, a: number, b: number): string {
    // Simplified LAB to RGB conversion
    // This is a basic conversion; for production, use a proper color library
    
    // LAB to XYZ
    let y = (l + 16) / 116;
    let x = a / 500 + y;
    let z = y - b / 200;

    const xyz = [x, y, z].map(v => {
      const v3 = v * v * v;
      return v3 > 0.008856 ? v3 : (v - 16 / 116) / 7.787;
    });

    x = xyz[0] * 95.047;
    y = xyz[1] * 100.000;
    z = xyz[2] * 108.883;

    // XYZ to RGB
    x /= 100;
    y /= 100;
    z /= 100;

    let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
    let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
    let bl = x * 0.0557 + y * -0.2040 + z * 1.0570;

    [r, g, bl] = [r, g, bl].map(v => {
      return v > 0.0031308 ? 1.055 * Math.pow(v, 1 / 2.4) - 0.055 : 12.92 * v;
    });

    return this.rgbToHex(r * 255, g * 255, bl * 255);
  }
}

export const importFormats = new ImportFormats();
